import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Plus, Trash2 } from "lucide-react";
import { baseurly } from '../../utils/apis.ts';
import { X_API_KEY } from '../../utils/apis.ts';
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";


const CreateVehicleModel: React.FC = () => {
  const [modelName, setModelName] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [yearMake, setYearMake] = useState<number | "">("");
  // New state for model image file and its path.
  const [modelImageFile, setModelImageFile] = useState<File | null>(null);
  const [modelImagePath, setModelImagePath] = useState<string>("");

  const [buildings, setBuildings] = useState<{ id: number; building_name: string }[]>([]);
  const [workStations, setWorkStations] = useState<{ id: number; work_station: string }[]>([]);
  const [workInstructions, setWorkInstructions] = useState<
    {
      id: number;
      buildingId: string;
      workStationId: string;
      title: string;
      description: string;
      file: string | null;
      drawing: string | null;
      video: string | null;
      isSubmitted: boolean;
    }[]
  >([
    {
      id: Date.now(),
      buildingId: "",
      workStationId: "",
      title: "",
      description: "",
      file: null,
      drawing: null,
      video: null,
      isSubmitted: false,
    },
  ]);
  const [vehicleModelId, setVehicleModelId] = useState<number | null>(null);
  const [isModelSubmitted, setIsModelSubmitted] = useState(false);
  const [buildProcess, setBuildProcess] = useState<{
    name: string;
    description: string;
    file: string | null;
    isSubmitted: boolean;
  }>({
    name: "",
    description: "",
    file: null,
    isSubmitted: false,
  });
  const [processDocs, setProcessDocs] = useState<ProcessDoc[]>([
    { id: Date.now(), buildingId: "", file: null, isSubmitted: false },
  ]);
  const navigate = useNavigate();

  interface ProcessDoc {
    id: number;
    buildingId: string;
    file: string | null;
    isSubmitted: boolean;
  }
  
  // Fetch buildings from API
  useEffect(() => {
    fetch(`${baseurly}/buildings`,{
               
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': X_API_KEY,
      },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setBuildings(data))
      .catch((err) => console.error("Error fetching buildings:", err));
  }, []);

  // Fetch workstations when a building is selected
  const fetchWorkStations = async (buildingId: string, index: number) => {
    if (!buildingId) return;
    try {
      const response = await fetch(`${baseurly}/buildings/${buildingId}/work_stations`, {
               
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': X_API_KEY,
        },
        credentials: "include",
      });
      const data = await response.json();
      setWorkStations(data);

      // Reset workstation selection when changing buildings
      setWorkInstructions((prev) =>
        prev.map((wi, i) => (i === index ? { ...wi, workStationId: "" } : wi))
      );
    } catch (error) {
      console.error("Error fetching workstations:", error);
    }
  };

  // Add a new work instruction section
  const handleAddWorkInstruction = () => {
    setWorkInstructions([
      ...workInstructions,
      {
        id: Date.now(),
        buildingId: "",
        workStationId: "",
        title: "",
        description: "",
        file: null,
        drawing: null,
        video: null,
        isSubmitted: false,
      },
    ]);
  };

  // Remove a work instruction section
  const handleRemoveWorkInstruction = (id: number) => {
    setWorkInstructions(workInstructions.filter((wi) => wi.id !== id));
  };

  // Upload file and get path for work instructions
  const handleFileUpload = async (file: File, index: number) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${baseurly}/upload/workInstruction`, {
        method: "POST",
        headers: { "X-API-KEY": X_API_KEY },
        body: formData,
      });

      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const filePath = await response.text();
      setWorkInstructions((prev) =>
        prev.map((wi, i) =>
          i === index ? { ...wi, file: JSON.parse(filePath) } : wi
        )
      );
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  // Upload build process file and get path
  const handleBuildProcessFileUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${baseurly}/upload/buildingProcess`, {
        method: "POST",
        headers: { "X-API-KEY": X_API_KEY },
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const filePath = await response.text();
      console.log("File path:", filePath);
      setBuildProcess((prev) => ({ ...prev, file: JSON.parse(filePath) }));
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  // Upload vehicle model image and get path immediately on file selection.
  const handleModelImageUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${baseurly}/upload/model_image`, {
        method: "POST",
        headers: { "X-API-KEY": X_API_KEY },
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Image upload failed");
      }

      const filePath = await response.text();
      const parsedFilePath = JSON.parse(filePath);
      setModelImagePath(parsedFilePath);
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  // Upload drawing and get path for work instructions
  const handleDrawingUpload = async (file: File, index: number) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${baseurly}/upload/drawing`, {
        method: "POST",
        headers: { "X-API-KEY": X_API_KEY },
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Drawing upload failed");
      }

      const filePath = await response.text();
      setWorkInstructions((prev) =>
        prev.map((wi, i) =>
          i === index ? { ...wi, drawing: JSON.parse(filePath) } : wi
        )
      );
    } catch (error) {
      console.error("Error uploading drawing:", error);
    }
  };

  // New: Upload video and get path for work instructions
  const handleVideoUpload = async (file: File, index: number) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${baseurly}/upload/video`, {
        method: "POST",
        headers: { "X-API-KEY": X_API_KEY },
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Video upload failed");
      }

      const filePath = await response.text();
      setWorkInstructions((prev) =>
        prev.map((wi, i) =>
          i === index ? { ...wi, video: JSON.parse(filePath) } : wi
        )
      );
    } catch (error) {
      console.error("Error uploading video:", error);
    }
  };

  // Submit vehicle model
  const handleSubmitVehicleModel = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!modelImagePath) {
      alert("Please wait for the image to finish uploading.");
      return;
    }

    const payload = {
      model_name: modelName,
      image: modelImagePath,
      vehicle_type: vehicleType,
      year_make: yearMake === "" ? 0 : Number(yearMake),
    };

    console.log("Payload:", payload);

    try {
      const response = await fetch(`${baseurly}/vehicle_model`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                   'X-API-KEY': X_API_KEY,
         },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to create vehicle model");

      const data = await response.json();
      setVehicleModelId(data.id);
      setIsModelSubmitted(true);
    } catch (error) {
      console.error("Error creating vehicle model:", error);
    }
  };

  // Submit individual work instruction
  const handleSubmitSingleWorkInstruction = async (index: number) => {
    if (!vehicleModelId) {
      alert("Please submit the vehicle model first");
      return;
    }

    const workInstruction = workInstructions[index];

    if (
      !workInstruction.buildingId ||
      !workInstruction.workStationId ||
      !workInstruction.title ||
      !workInstruction.description
    ) {
      alert("Please fill in all required fields");
      return;
    }

    if (!workInstruction.file) {
      return;
    }

    try {
      const payload = {
        author: "Admin",
        author_email: "admin@example.com",
        title: workInstruction.title,
        description: workInstruction.description,
        file: workInstruction.file,
        drawing: workInstruction.drawing,
        video: workInstruction.video,
        vehicle_model_id: vehicleModelId,
        work_station_id: workInstruction.workStationId
          ? Number(workInstruction.workStationId)
          : 0,
      };

      console.log("Work Instruction File:", workInstruction.file);
      console.log("Drawing File:", workInstruction.drawing);
      console.log("Video File:", workInstruction.video);

      const response = await fetch(`${baseurly}/work_instruction`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                    'X-API-KEY': X_API_KEY,
         },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to submit work instruction");

      // Mark this work instruction as submitted
      setWorkInstructions((prev) =>
        prev.map((wi, i) => (i === index ? { ...wi, isSubmitted: true } : wi))
      );

      alert("Work instruction submitted successfully!");
    } catch (error) {
      console.error("Error submitting work instruction:", error);
      alert("Failed to submit work instruction");
    }
  };

  // Submit build process
  const handleSubmitBuildProcess = async () => {
    if (!vehicleModelId) {
      alert("Please submit the vehicle model first");
      return;
    }

    if (!buildProcess.name || !buildProcess.description || !buildProcess.file) {
      alert("Please fill in all required fields and upload a file");
      return;
    }

    try {
      const payload = {
        name: buildProcess.name,
        description: buildProcess.description,
        file: buildProcess.file,
        vehicle_model_id: vehicleModelId,
      };

      const response = await fetch(`${baseurly}/build_process`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                    'X-API-KEY': X_API_KEY,
         },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to submit build process");

      // Mark build process as submitted
      setBuildProcess((prev) => ({ ...prev, isSubmitted: true }));

      alert("Build process submitted successfully!");
    } catch (error) {
      console.error("Error submitting build process:", error);
      alert("Failed to submit build process");
    }
  };

  // Handle process document file upload
  const handleProcessDocFileUpload = async (file: File, index: number) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${baseurly}/upload/processDoc`, {
        method: "POST",
        headers: { "X-API-KEY": X_API_KEY },
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("File upload failed");
      }

      const filePath = await response.text();

      setProcessDocs((prev) =>
        prev.map((doc, i) => (i === index ? { ...doc, file: JSON.parse(filePath) } : doc))
      );
    } catch (error) {
      console.error("Error uploading process document:", error);
    }
  };

  // Add new process document section
  const handleAddProcessDoc = () => {
    setProcessDocs([
      ...processDocs,
      { id: Date.now(), buildingId: "", file: null, isSubmitted: false },
    ]);
  };

  // Remove process document section
  const handleRemoveProcessDoc = (id: number) => {
    setProcessDocs(processDocs.filter((doc) => doc.id !== id));
  };

  // Submit single process document
  const handleSubmitSingleProcessDoc = async (index: number) => {
    if (!vehicleModelId) {
      alert("Please submit the vehicle model first");
      return;
    }

    const processDoc = processDocs[index];

    if (!processDoc.buildingId || !processDoc.file) {
      alert("Please select a building and upload a file");
      return;
    }

    try {
      const payload = {
        building_id: Number(processDoc.buildingId),
        vehicle_model_id: vehicleModelId,
        file: processDoc.file,
      };

      const response = await fetch(`${baseurly}/process_docs`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                    'X-API-KEY': X_API_KEY,
         },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to submit process document");

      // Mark this process document as submitted
      setProcessDocs((prev) =>
        prev.map((doc, i) => (i === index ? { ...doc, isSubmitted: true } : doc))
      );

      alert("Process document submitted successfully!");
    } catch (error) {
      console.error("Error submitting process document:", error);
      alert("Failed to submit process document");
    }
  };

  return (
    <div className="min-h-screen h-screen bg-blue-900">
      <div className="p-4">
        <img
          src={digitalOceanImageUrl("new%20logos/Asset%204.png")}
          alt="KMC Logo"
          className="w-16 h-auto ml-auto h-24 w-27"
        />
      </div>
      <div className="p-6 flex justify-between items-center">
        <h1 className="text-white text-lg font-medium">Create Vehicle Model</h1>
      </div>

      <div className="bg-white rounded-3xl p-6">
        <form onSubmit={handleSubmitVehicleModel}>
          <h2 className="text-blue-900 text-xl font-bold mb-4">Vehicle Model Information</h2>
          <input
            type="text"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            placeholder="Model Name"
            className="w-full p-2 border bg-sky-200 rounded mb-4"
            required
          />
          <input
            type="text"
            value={vehicleType}
            onChange={(e) => setVehicleType(e.target.value)}
            placeholder="Vehicle Type"
            className="w-full p-2 border bg-sky-200 rounded mb-4"
            required
          />
          <input
            type="number"
            value={yearMake}
            onChange={(e) => setYearMake(e.target.value === "" ? "" : Number(e.target.value))}
            placeholder="Year Make"
            className="w-full p-2 border bg-sky-200 rounded mb-4"
          />
          {/* New: Vehicle Model Image Field */}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];
                setModelImageFile(file);
                setModelImagePath("");
                handleModelImageUpload(file);
              }
            }}
            className="w-full p-2 border bg-sky-200 rounded mb-4"
            required
          />
          <button
            type="submit"
            disabled={isModelSubmitted}
            className={`w-full text-white px-3 py-3 rounded ${
              isModelSubmitted ? "bg-gray-500" : "bg-blue-900 hover:bg-blue-800"
            }`}
          >
            {isModelSubmitted ? "Model Submitted" : "Save Model"}
          </button>
        </form>

        <h2 className="text-blue-900 text-xl font-bold mt-6">Build Process</h2>
        <div className="border p-4 rounded-lg mt-4 bg-gray-100">
          <input
            type="text"
            value={buildProcess.name}
            onChange={(e) => setBuildProcess((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Build Process Name"
            className="w-full p-2 border rounded mb-2"
          />
          <textarea
            value={buildProcess.description}
            onChange={(e) => setBuildProcess((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Description"
            className="w-full p-2 border rounded mb-2"
          />
          <input
            type="file"
            onChange={(e) => e.target.files && handleBuildProcessFileUpload(e.target.files[0])}
            className="mb-2"
          />
          <button
            onClick={handleSubmitBuildProcess}
            disabled={buildProcess.isSubmitted}
            className={`w-full text-white px-3 py-2 rounded ${
              buildProcess.isSubmitted ? "bg-gray-500" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {buildProcess.isSubmitted ? "Build Process Submitted" : "Submit Build Process"}
          </button>
        </div>

        <h2 className="text-blue-900 text-xl font-bold mt-6">Process Documents</h2>
        {processDocs.map((doc, index) => (
          <div key={doc.id} className="border p-4 rounded-lg mt-4 bg-gray-100 relative">
            <select
              value={doc.buildingId}
              className="w-full p-2 border bg-white rounded mb-2"
              onChange={(e) => {
                setProcessDocs((prev) =>
                  prev.map((d, i) => (i === index ? { ...d, buildingId: e.target.value } : d))
                );
              }}
            >
              <option value="">Select Building</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.building_name}
                </option>
              ))}
            </select>
            <button
              onClick={() => handleRemoveProcessDoc(doc.id)}
              className="absolute top-2 right-2 text-red-500"
            >
              <Trash2 />
            </button>
            <input
              type="file"
              onChange={(e) => e.target.files && handleProcessDocFileUpload(e.target.files[0], index)}
              className="mb-2"
            />
            <button
              onClick={() => handleSubmitSingleProcessDoc(index)}
              disabled={doc.isSubmitted}
              className={`w-full text-white px-3 py-2 rounded ${
                doc.isSubmitted ? "bg-gray-500" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {doc.isSubmitted ? "Document Submitted" : "Submit This Document"}
            </button>
          </div>
        ))}
        <button
          onClick={handleAddProcessDoc}
          className="w-full bg-blue-900 text-white px-3 py-3 rounded mt-4"
        >
          <Plus /> Add Process Document
        </button>

        <h2 className="text-blue-900 text-xl font-bold mt-6">Work Instructions</h2>
        {workInstructions.map((wi, index) => (
          <div key={wi.id} className="border p-4 rounded-lg mt-4 bg-gray-100 relative">
            <select
              value={wi.buildingId}
              className="w-full p-2 border bg-white rounded mb-2"
              onChange={(e) => {
                fetchWorkStations(e.target.value, index);
                setWorkInstructions((prev) =>
                  prev.map((w, i) => (i === index ? { ...w, buildingId: e.target.value } : w))
                );
              }}
            >
              <option value="">Select Building</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.building_name}
                </option>
              ))}
            </select>
            <select
              value={wi.workStationId}
              className="w-full p-2 border bg-white rounded mb-2"
              onChange={(e) =>
                setWorkInstructions((prev) =>
                  prev.map((w, i) =>
                    i === index ? { ...w, workStationId: e.target.value } : w
                  )
                )
              }
            >
              <option value="">Select Work Station</option>
              {workStations.map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws.work_station}
                </option>
              ))}
            </select>
            <button
              onClick={() => handleRemoveWorkInstruction(wi.id)}
              className="absolute top-2 right-2 text-red-500"
            >
              <Trash2 />
            </button>
            <input
              type="text"
              value={wi.title}
              onChange={(e) =>
                setWorkInstructions((prev) =>
                  prev.map((w, i) => (i === index ? { ...w, title: e.target.value } : w))
                )
              }
              placeholder="Work Instruction Title"
              className="w-full p-2 border rounded mb-2"
            />
            <textarea
              value={wi.description}
              onChange={(e) =>
                setWorkInstructions((prev) =>
                  prev.map((w, i) => (i === index ? { ...w, description: e.target.value } : w))
                )
              }
              placeholder="Description"
              className="w-full p-2 border rounded mb-2"
            />
            <input
              type="file"
              onChange={(e) => e.target.files && handleFileUpload(e.target.files[0], index)}
              className="mb-2"
            />
            {/* Drawing Upload */}
            <p className="gap-3 py-4 font-semibold text-blue-900">Add Drawing (pdf)</p>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => e.target.files && handleDrawingUpload(e.target.files[0], index)}
              className="mb-2"
            />
            {/* New: Video Upload */}
            <p className="gap-3 py-4 font-semibold text-blue-900">Add Video (optional)</p>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => e.target.files && handleVideoUpload(e.target.files[0], index)}
              className="mb-2"
            />
            <button
              onClick={() => handleSubmitSingleWorkInstruction(index)}
              disabled={wi.isSubmitted}
              className={`w-full text-white px-3 py-2 rounded ${
                wi.isSubmitted ? "bg-gray-500" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {wi.isSubmitted ? "Instruction Submitted" : "Submit This Instruction"}
            </button>
          </div>
        ))}
        <button
          onClick={handleAddWorkInstruction}
          className="w-full bg-blue-900 text-white px-3 py-3 rounded mt-4"
        >
          <Plus /> Add Work Instruction
        </button>
        <button
          onClick={() => navigate("/admin-vehicle-models")}
          className="w-full bg-green-600 text-white px-3 py-3 rounded mt-4"
        >
          Return to Vehicle Models
        </button>
      </div>
    </div>
  );
};

export default CreateVehicleModel;
